import { z } from "zod";

export const SiteReadyStatusSchema = z.enum(["green", "amber", "red"]);
export type SiteReadyStatus = z.infer<typeof SiteReadyStatusSchema>;

export const SlackContextFindingSeveritySchema = z.enum([
  "info",
  "warning",
  "blocker",
]);
export type SlackContextFindingSeverity = z.infer<
  typeof SlackContextFindingSeveritySchema
>;

export const SlackContextFindingCategorySchema = z.enum([
  "scope_change",
  "hot_works",
  "scaffold",
  "excavation",
  "plant",
  "delivery",
  "incident",
  "weather",
  "access",
  "unknown",
]);
export type SlackContextFindingCategory = z.infer<
  typeof SlackContextFindingCategorySchema
>;

export const SlackContextFindingSchema = z.object({
  severity: SlackContextFindingSeveritySchema,
  category: SlackContextFindingCategorySchema,
  summary: z.string().min(1).max(280),
  sourcePermalink: z.string().url().optional(),
  sourceChannelName: z.string().min(1).max(80).optional(),
  createdAt: z.string().datetime().optional(),
});
export type SlackContextFinding = z.infer<typeof SlackContextFindingSchema>;

export const SlackRtsHitSchema = z.object({
  id: z.string().min(1).optional(),
  channelId: z.string().min(1).optional(),
  channelName: z.string().min(1).max(80).optional(),
  text: z.string().max(2000),
  permalink: z.string().url().optional(),
  createdAt: z.string().datetime().optional(),
});
export type SlackRtsHit = z.infer<typeof SlackRtsHitSchema>;

export const SiteReadyProjectSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  siteAddress: z.string().min(1),
  status: z.string().min(1),
  startDate: z.string().datetime().nullable().optional(),
});
export type SiteReadyProject = z.infer<typeof SiteReadyProjectSchema>;

export const SiteReadyActionSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(["open_url", "button"]),
  label: z.string().min(1).max(80),
  url: z.string().url().optional(),
});
export type SiteReadyAction = z.infer<typeof SiteReadyActionSchema>;

export const SiteReadyItemSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  detail: z.string().min(1),
  source: z.enum(["sitebook", "slack"]),
  severity: z.enum(["info", "warning", "blocker"]),
  cta: z.string().min(1).optional(),
  url: z.string().url().optional(),
});
export type SiteReadyItem = z.infer<typeof SiteReadyItemSchema>;

export const SiteReadyLinksSchema = z.object({
  projectUrl: z.string().url(),
  documentsUrl: z.string().url(),
  auditPackUrl: z.string().url(),
  shareUrl: z.string().url().optional(),
});
export type SiteReadyLinks = z.infer<typeof SiteReadyLinksSchema>;

export const SiteReadyDigestSchema = z.object({
  checkedAt: z.string().datetime(),
  project: SiteReadyProjectSchema,
  status: SiteReadyStatusSchema,
  blockers: z.array(SiteReadyItemSchema),
  warnings: z.array(SiteReadyItemSchema),
  readyItems: z.array(SiteReadyItemSchema),
  slackFindings: z.array(SlackContextFindingSchema),
  actions: z.array(SiteReadyActionSchema),
  links: SiteReadyLinksSchema,
});
export type SiteReadyDigest = z.infer<typeof SiteReadyDigestSchema>;

export const SiteReadyRiskFindingsResponseSchema = z.object({
  findings: z.array(SlackContextFindingSchema),
  sourceCount: z.number().int().nonnegative(),
});
export type SiteReadyRiskFindingsResponse = z.infer<
  typeof SiteReadyRiskFindingsResponseSchema
>;
